import * as React from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import type { CashDetail } from "@/types/finance";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import { Button } from "@/components/ui/button";
import { PencilLine, ArrowLeft } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";

import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

// Services
import { getCash } from "@/services/finance";

// modal
import { ModalRequireEditDoc } from "@/components/ModalRequireEditDoc";
import calculateDaysFromNow from "@/utils/calculateDaysFromNow";
import { RequireEditDocs } from "@/services/require-edit-docs";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/cash-flow-indirect-method/"
)({
  component: RouteComponent, // Use App instead of RouteComponent
});

function RouteComponent() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem("companyId");
  const year = Number(localStorage.getItem("year"));
  const theType = "INDIRECT";
  const { company_financial_report } = useAccessPermission();
  const [open, setOpen] = React.useState(false);
  const { toast } = useToast();
  React.useEffect(() => {
    if (!year) {
      navigate({ to: "/report/finance/company/cash-flow-indirect-method/add" });
    }
  }, [year, navigate]);

  const queryClient = useQueryClient();

  const queries = useQueries({
    queries: [
      {
        queryKey: ["getCashIndirect", companyId, year],
        queryFn: () => getCash(companyId as string, year, theType),
        enabled: !!companyId && !!year,
      },
      {
        queryKey: ["getCashIndirect", companyId, year - 1],
        queryFn: () => getCash(companyId as string, year - 1, theType),
        enabled: !!companyId && !!year,
      },
    ],
  });

  const [
    { data, isLoading, error },
    { data: lastdata, isLoading: lastisLoading },
  ] = queries;

  React.useEffect(() => {
    if (!data?.data && !isLoading) {
      navigate({ to: "/report/finance/company/cash-flow-indirect-method/add" });
    }
  }, [data, navigate, isLoading]);
  const [unit, setUnit] = React.useState("1");
  const requireEditMutation = useMutation({
    mutationFn: (data: {
      editId: string;
      editRequestType: string;
      pendingReason: string;
      documentFile: string;
      editRequestStatus: string;
    }) => RequireEditDocs(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getCashIndirect", companyId, year],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ທ່ານໄດ້ສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານແລ້ວ",
        duration: 5000,
        variant: "success",
      });
      setOpen(false);
    },
    onError: () => {
      toast({
        title: "ຜິດພາດ",
        description:
          "ການສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານ ບໍ່ສາມາດເຮັດໄດ້ ກະລຸນາລອງໃໝ່ ຫຼື ຕິດຕໍ່ ຜູ້ໃຊ້ ຖ້າ ວ່າ ຍັງ ມີ ບັນ ຫາ",
        duration: 5000,
        variant: "destructive",
      });
    },
  });

  const numericUnit = Number(unit);

  const dataLoading = isLoading || lastisLoading;

  const cashData = data?.data;
  const daysFromNow = calculateDaysFromNow(cashData?.updatedAt);

  const getSectionTitle = (type: string) => {
    const titles: Record<string, string> = {
      OPERATING_A: "ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ",
      INVESTING_B: "ກະແສເງິນສົດທີ່ຕິດພັນກັບການລົງທຶນ",
      FINANCING_C: "ກະແສເງິນສົດທີ່ຕິດພັນກັບການປະກອບທຶນ",
      CASH_YEARLY: "ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ",
      // ANNUAL_NET: "ກຳໄລປະຈຳປີ",
    };
    return titles[type];
  };

  const thisYearDetails = [
    {
      type: "OPERATING_A",
      data: data?.data?.cashDetails?.filter(
        (detail: CashDetail) => detail.cashDetailType === "OPERATING_A"
      ),
    },
    {
      type: "INVESTING_B",
      data: data?.data?.cashDetails?.filter(
        (detail: CashDetail) => detail.cashDetailType === "INVESTING_B"
      ),
    },
    {
      type: "FINANCING_C",
      data: data?.data?.cashDetails?.filter(
        (detail: CashDetail) => detail.cashDetailType === "FINANCING_C"
      ),
    },
    {
      type: "CASH_YEARLY",
      data: data?.data?.cashDetails?.filter(
        (detail: CashDetail) => detail.cashDetailType === "CASH_YEARLY"
      ),
    },
  ];

  const lastYearDetails = [
    {
      type: "OPERATING_A",
      data: lastdata?.data?.cashDetails?.filter(
        (detail: CashDetail) => detail.cashDetailType === "OPERATING_A"
      ),
    },
    {
      type: "INVESTING_B",
      data: lastdata?.data?.cashDetails?.filter(
        (detail: CashDetail) => detail.cashDetailType === "INVESTING_B"
      ),
    },
    {
      type: "FINANCING_C",
      data: lastdata?.data?.cashDetails?.filter(
        (detail: CashDetail) => detail.cashDetailType === "FINANCING_C"
      ),
    },
    {
      type: "CASH_YEARLY",
      data: lastdata?.data?.cashDetails?.filter(
        (detail: CashDetail) => detail.cashDetailType === "CASH_YEARLY"
      ),
    },
  ];

  const calculateTotal = (
    cashDetails: {
      cashDetailType: string;
      amount: string;
      cashDataType?: string;
    }[],
    type: string
  ): number => {
    return cashDetails
      .filter((detail) => detail?.cashDetailType === type)
      .reduce((sum, detail) => {
        // Convert the amount to a processable number
        let numericAmount = 0;

        if (detail?.amount) {
          // Remove commas and parentheses
          const cleanAmount = String(detail?.amount).replace(/[,()]/g, "");
          numericAmount = Number(cleanAmount);

          // If original amount was in parentheses (expense) or cashDataType is EXPENSE, make it negative
          if (
            detail?.cashDataType === "EXPENSE" ||
            String(detail?.amount).startsWith("(")
          ) {
            numericAmount = -Math.abs(numericAmount);
          }
        }

        return sum + numericAmount;
      }, 0);
  };

  const totalOperatingA = React.useMemo(
    () =>
      calculateTotal(
        thisYearDetails.flatMap((item) => item.data),
        "OPERATING_A"
      ),
    [thisYearDetails]
  );

  const totalLastOperatingA = React.useMemo(
    () =>
      calculateTotal(
        lastYearDetails.flatMap((item) => item.data),
        "OPERATING_A"
      ),
    [lastYearDetails]
  );

  const totalInvestingB = React.useMemo(
    () =>
      calculateTotal(
        thisYearDetails.flatMap((item) => item.data),
        "INVESTING_B"
      ),
    [thisYearDetails]
  );

  const totalLastYearInvestingB = React.useMemo(
    () =>
      calculateTotal(
        lastYearDetails.flatMap((item) => item.data),
        "INVESTING_B"
      ),
    [lastYearDetails]
  );

  const totalFinancingC = React.useMemo(
    () =>
      calculateTotal(
        thisYearDetails.flatMap((item) => item.data),
        "FINANCING_C"
      ),
    [thisYearDetails]
  );

  const totalLastFinancingC = React.useMemo(
    () =>
      calculateTotal(
        lastYearDetails.flatMap((item) => item.data),
        "FINANCING_C"
      ),
    [lastYearDetails]
  );

  const { thisYearCashYearly, lastYearCashYearly } = React.useMemo(() => {
    const thisYearCashYearlyItems = thisYearDetails
      .flatMap((item) => item?.data)
      .filter((detail) => detail?.cashDetailType === "CASH_YEARLY");

    const lastYearCashYearlyItems = lastYearDetails
      .flatMap((item) => item?.data)
      .filter((detail) => detail?.cashDetailType === "CASH_YEARLY");

    const getNumericAmount = (detail: any) => {
      if (!detail?.amount) return 0;

      const cleanAmount = String(detail?.amount).replace(/[,()]/g, "");
      return Number(cleanAmount);
    };

    const thisYearFirstAmount = getNumericAmount(thisYearCashYearlyItems[0]);
    const thisYearLastAmount = getNumericAmount(
      thisYearCashYearlyItems[thisYearCashYearlyItems.length - 1]
    );

    const lastYearFirstAmount = getNumericAmount(lastYearCashYearlyItems[0]);
    const lastYearLastAmount = getNumericAmount(
      lastYearCashYearlyItems[lastYearCashYearlyItems.length - 1]
    );

    const thisYearCashYearly = thisYearLastAmount - thisYearFirstAmount;
    const lastYearCashYearly = lastYearLastAmount - lastYearFirstAmount;

    return { thisYearCashYearly, lastYearCashYearly };
  }, [thisYearDetails, lastYearDetails]);

  const formatNumberWithCommas = (
    value: number | string,
    type: "INCOME" | "EXPENSE" | undefined
  ) => {
    if (!value || value === "-") return "0.00";

    // Convert to string and remove commas
    const stringValue = String(value).replace(/,/g, "");

    // Get absolute value for display
    const numericValue = Math.abs(Number(stringValue.replace(/[()]/g, "")));

    // If the value is invalid (NaN), return formatted zero
    if (Number.isNaN(numericValue)) {
      return "0.00";
    }

    // Format with commas and 2 decimal places
    const formattedValue = numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    // Return with parentheses for EXPENSE type
    return type === "EXPENSE" ? `(${formattedValue})` : formattedValue;
  };

  const renderSummaryRow = (type: string) => {
    switch (type) {
      case "OPERATING_A":
        return (
          <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
            <TableCell>ກະແສເງິນສົດລວມ ທີ່ຕິດພັນກັບການທຸລະກິດ (ກ)</TableCell>
            <TableCell className="text-right">
              {(totalOperatingA / numericUnit).toLocaleString()}
            </TableCell>
            <TableCell className="text-right">
              {(totalLastOperatingA / numericUnit).toLocaleString()}
            </TableCell>
            <TableCell> </TableCell>
          </TableRow>
        );

      case "INVESTING_B":
        return (
          <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
            <TableCell>ກະແສເງິນສົດລວມສຸດທິ ຈາກການລົງທຶນ (ຂ)</TableCell>
            <TableCell className="text-right">
              {(totalInvestingB / numericUnit).toLocaleString()}
            </TableCell>
            <TableCell className="text-right">
              {(totalLastYearInvestingB / numericUnit).toLocaleString()}
            </TableCell>
            <TableCell> </TableCell>
          </TableRow>
        );

      case "FINANCING_C":
        return (
          <>
            <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
              <TableCell>ກະແສເງິນສົດລວມສຸດທິ ຈາກການປະກອບທຶນ (ຄ)</TableCell>
              <TableCell className="text-right">
                {(totalFinancingC / numericUnit).toLocaleString()}
              </TableCell>
              <TableCell className="text-right">
                {(totalLastFinancingC / numericUnit).toLocaleString()}
              </TableCell>
              <TableCell> </TableCell>
            </TableRow>

            <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
              <TableCell>
                ສ່ວນປ່ຽນແປງກະແສເງິນສົດລວມໃນປີ ( ກ + ຂ + ຄ ){" "}
              </TableCell>
              <TableCell className="text-right">
                {(
                  (totalOperatingA + totalInvestingB + totalFinancingC) /
                  numericUnit
                ).toLocaleString()}
              </TableCell>
              <TableCell className="text-right">
                {(
                  (totalLastOperatingA +
                    totalLastYearInvestingB +
                    totalLastFinancingC) /
                  numericUnit
                ).toLocaleString()}
              </TableCell>
              <TableCell> </TableCell>
            </TableRow>
          </>
        );

      default:
        break;
    }
  };

  const renderRows = (thisYearData: CashDetail[], lastYearData: CashDetail[]) => {
    const mergedDetails = thisYearData?.map((thisYearDetail, index) => {
      // Find corresponding last year data based on 'name'
      const lastYearDetail = lastYearData?.find(
        (last) => last.name === thisYearDetail.name
      );

      // If no matching last year data, set it as 0.
      const lastYearAmount = lastYearDetail ? lastYearDetail?.amount : 0;
      const thisYearAmount = thisYearDetail?.amount;

      return (
        <TableRow key={index}>
          <TableCell className="border">{thisYearDetail.name}</TableCell>
          <TableCell className="text-right border">
            {formatNumberWithCommas(
              thisYearAmount / numericUnit,
              thisYearDetail?.cashDataType
            )}
          </TableCell>
          <TableCell className="text-right border">
            {formatNumberWithCommas(
              lastYearAmount / numericUnit,
              lastYearDetail?.cashDataType
            )}
          </TableCell>
          <TableCell className="border">{thisYearDetail.remark}</TableCell>
        </TableRow>
      );
    });
    return mergedDetails;
  };

  const handleRequireEdit = ({
    reason,
    file,
  }: {
    reason: string;
    file: string;
  }) => {
    if (!data?.data?.id) return;
    requireEditMutation.mutate({
      editId: data?.data?.id,
      editRequestType: "INDIRECT_CASH_FLOW_REPORT",
      pendingReason: reason,
      documentFile: file,
      editRequestStatus: "REQUEST",
    });
  };

  if (dataLoading) {
    return <DinamixSkeletonTable />;
  }
  if (error) return <div>Error loading data</div>;

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: { page: 1, startYear: 0, endYear: 0 },
              })
            }
            className="flex items-center gap-[4px] mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>

          <CardTitle>
            ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {cashData?.yearly}
          </CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>

          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">
            {daysFromNow <= 3 ? company_financial_report.isUpdate && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/report/finance/company/cash-flow-indirect-method/edit",
                  })
                }
                variant="secondary"
                className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            ) :
              company_financial_report.isUpdate && (
                cashData?.editRequestStatus === "REQUEST" ? (
                  <p className="text-red-500">ກະລຸນາລໍຖ້າການກວດສອບ</p>
                ) : (
                  <Button
                    onClick={() => setOpen(true)}
                    variant="secondary"
                    className="flex items-center border border-blue-500 gap-x-1 w-[120px] p-2 text-blue-500"
                  >
                    <PencilLine /> ຂໍແກ້ໄຂເອກະສານ
                  </Button>
                ))}
          </div>
          <ModalRequireEditDoc
            isOpen={open}
            onClose={() => setOpen(false)}
            onConfirm={handleRequireEdit}
          />
        </CardHeader>
        <CardContent>
          {/* <div className="flex flex-col gap-2 mb-4">
						<Label>ຫົວໜ່ວຍເງິນ</Label>
						<Select onValueChange={(value) => setUnit(Number(value))}>
							<SelectTrigger className="w-[150px]  ">
								<SelectValue placeholder="ເລືອກການສະແດງ" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="1">1</SelectItem>
								<SelectItem value="1000">1,000</SelectItem>
								<SelectItem value="1000000">1,000,000</SelectItem>
							</SelectContent>
						</Select>
					</div> */}

          <div className="mb-4">
            <NumberFormatSelector unit={unit} setUnit={setUnit} />
          </div>

          <Table className="mb-4">
            <TableHeader>
              <TableRow className="[&>*]:font-bold	[&>*]:text-black [&>*]:whitespace-nowrap">
                <TableHead className="border">ເນຶ້ອໃນລາຍງານ</TableHead>
                <TableHead className="border">
                  ມູນຄ່າຍັງເຫຼືອ ({year})
                </TableHead>
                <TableHead className="border">
                  ມູນຄ່າຍັງເຫຼືອ ({year - 1})
                </TableHead>
                <TableHead className="border">ໝາຍເຫດ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Map over each section (OPERATING_A, INVESTING_B, etc.) */}
              {thisYearDetails.map((section, index) => (
                <React.Fragment key={section.type}>
                  <TableRow className="bg-[#216C4C]/20 font-bold">
                    <TableCell colSpan={4}>
                      {getSectionTitle(section.type)}
                    </TableCell>
                  </TableRow>
                  {renderRows(section.data, lastYearDetails[index].data)}
                  {renderSummaryRow(section.type)}
                </React.Fragment>
              ))}

              <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                <TableCell>ສ່ວນປ່ຽນແປງ ເງິນສົດລວມ</TableCell>
                <TableCell className="text-right">
                  {(thisYearCashYearly / numericUnit).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(lastYearCashYearly / numericUnit).toLocaleString()}
                </TableCell>
                <TableCell> </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {cashData?.docFile
            ? company_financial_report.isDownload && (
              <FileHandleViewer fileUrl={cashData?.docFile} />
            )
            : null}
        </CardContent>
      </Card>
    </section>
  );
}
